// Redux
import { AppThunk } from '@redux/configureStore';
import { createSlice } from '@reduxjs/toolkit';
// Models
import { IElectronicPayrollState } from './types';
import { IConfirmResponse, IElectronicPayrollResponse, IGeneratePayroll, ISaveInformation } from '@models/ElectronicPayroll';
// Api
import { setInformation } from '@redux/company/company.slice';
import {
    apiGetCheckEmployerStatus,
    apiGetPayroll,
    apiGetProcessPayroll,
    apiGetElectronicPayrollToken,
    apiGetYearPeriodTransmit,
    apiPostGeneratePayroll,
    apiPostSaveInformation,
} from '@api/electronicPayroll';
// Constants
import { CurrentTabs } from '@pages/electronic-payroll/constants/Tabs';
// Utils
import { checkEmployerCode } from '@pages/electronic-payroll/utils/checkEmployerCode';
import { getApiCarouselPeriods, setCurrentIndex } from '@redux/periods/periods.slice';
import { PeriodicityTypes } from '@constants/Periodicity';
import {
    CONFIRMED_STATUS,
    ELECTRONIC_PAYROLL_BUY_NEW_PLANS,
    ELECTRONIC_PAYROLL_INSUFFICIENT_DOCUMENTS,
    ELECTRONIC_PAYROLL_WITHOUT_DOCUMENTS,
    PARTIALLY_TRANSMITED,
    REFUSED,
} from '@pages/electronic-payroll/constants/Transmit';
import moment from 'moment';
import { apiGetPlansEmployee } from '@api/plans';
import { APPROVED } from '@constants/Onboarding';
import { setPlansEmployee } from '@redux/plans/plans.slice';
import { PayrollResponseCodes } from '@constants/responseCodes/payroll';
import { IPlanApiResponse } from '@redux/plans/types';
import { PaymentResponseCodes } from '@constants/responseCodes/payment';

export const initialState: IElectronicPayrollState = {
    error: '',
    payroll: null,
    startTransmit: false,
    confirmed: false,
    hasElectronicPayroll: false,
    currentTab: CurrentTabs.ENABLE_PAYROLL,
    errorModal: false,
    errorGeneratePayrollModal: false,
    responseConfirm: {
        idTrack: null,
        successful: false,
        descripcion: [],
    },
    yearPeriodId: '',
    token: '',
    transmitted: false,
    periods: [],
    isAlreadyTransmitedThisPeriod: false,
};

const electronicPayrollSlice = createSlice({
    name: 'electronicPayroll',
    initialState,
    reducers: {
        setPayroll: (state, action) => {
            state.payroll = action.payload;
        },
        setStartTransmit: (state, action) => {
            state.startTransmit = action.payload;
        },
        setConfirmed: (state, action) => {
            state.confirmed = action.payload;
        },
        setHasElectronicPayroll: (state, action) => {
            state.hasElectronicPayroll = action.payload;
        },
        setCurrentTab: (state, action) => {
            state.currentTab = action.payload;
        },
        setErrorModal: state => {
            state.errorModal = !state.errorModal;
        },
        setErrorGeneratePayrollModal: state => {
            state.errorGeneratePayrollModal = !state.errorGeneratePayrollModal;
        },
        setResponseConfirm: (state, action) => {
            state.responseConfirm = action.payload;
        },
        setYearPeriodId: (state, action) => {
            state.yearPeriodId = action.payload.id;
        },
        setToken: (state, action) => {
            state.token = action.payload;
        },
        setTransmitted: (state, action) => {
            state.transmitted = action.payload;
        },
        setPeriods: (state, action) => {
            state.periods = action.payload;
        },
        setIsAlreadyTransmitedThisPeriod: (state, action) => {
            state.isAlreadyTransmitedThisPeriod = action.payload;
        },
        failedRequest: (state, action) => {
            state.error = action.payload;
        },
    },
});

/**
 * Action to save information from electronic payroll
 */
export const postSocialBenefitsConfiguration = (data: ISaveInformation): AppThunk => {
    return async (dispatch): Promise<void> => {
        try {
            const employerInformation = await apiPostSaveInformation(data);
            dispatch(setInformation(employerInformation));
        } catch {
            dispatch(failedRequest('Error request data'));
        }
    };
};

/**
 * Action to generate payroll from electronic payroll
 */
export const postGeneratePayroll = (yearPeriodId: string): AppThunk => {
    return async (dispatch): Promise<void> => {
        try {
            if (yearPeriodId) {
                const response = await apiPostGeneratePayroll({ year_period_id: yearPeriodId });
                if (response && (response as { errorValidacionPila: boolean })?.errorValidacionPila) {
                    dispatch(setErrorGeneratePayrollModal());
                    dispatch(setPayroll(null));
                    return;
                }
                if (response && (response as { id: string })?.id) {
                    dispatch(setPayroll(response));
                    return;
                }
                dispatch(setPayroll(null));
            }
        } catch {
            dispatch(failedRequest('Error request data'));
        }
    };
};

/**
 * Action to get process payroll from electronic payroll
 */
export const getProcessPayroll = (id: string, yearPeriodId: string): AppThunk => {
    return async (dispatch): Promise<void> => {
        try {
            const response = await apiGetProcessPayroll(id);
            const data = response?.response_service ? JSON.parse(response?.response_service || '') : response;
            if (data) {
                if (data?.successful && data?.idTrack) {
                    dispatch(setIsAlreadyTransmitedThisPeriod(true));
                    dispatch(setConfirmed(true));
                    dispatch(getPayroll(yearPeriodId));
                    return;
                }
                if (!data?.successful) {
                    dispatch(setResponseConfirm(response));
                    dispatch(setIsAlreadyTransmitedThisPeriod(false));
                    if (data?.descripcion) dispatch(setErrorModal());
                    return;
                }
                dispatch(setConfirmed(false));
            }
        } catch {
            dispatch(failedRequest('Error request data'));
        }
    };
};

/**
 * Action to get process payroll from electronic payroll
 */
export const getPayroll = (yearPeriodId: string): AppThunk => {
    return async (dispatch): Promise<void> => {
        try {
            if (yearPeriodId) {
                const response = await apiGetPayroll(yearPeriodId);
                if (typeof response !== 'string' && response && (response as { id: string }).id) {
                    dispatch(setPayroll(response));
                    return;
                }
                dispatch(setPayroll(null));
                dispatch(setConfirmed(false));
            }
        } catch {
            dispatch(failedRequest('Error request data'));
        }
    };
};

/**
 * Function that get the electronic payroll from a determined period
 * @param yearPeriodId id of the period to be query
 */
export const getTransmitedPayrollByPeriod = (yearPeriodId: string): AppThunk => {
    return async (dispatch, getState): Promise<void> => {
        const {
            electronicPayroll: { errorModal },
        } = getState();
        try {
            if (yearPeriodId) {
                if (errorModal) dispatch(setErrorModal());
                dispatch(setIsAlreadyTransmitedThisPeriod(false));
                dispatch(setPayroll(null));
                const response: IGeneratePayroll | string = await apiGetPayroll(yearPeriodId);
                if (response && typeof response !== 'string') {
                    const transmisionCode = response.status_id.toString();
                    const codeValidation = CONFIRMED_STATUS.some(elem => elem == transmisionCode);
                    if (!codeValidation) return;
                    dispatch(setIsAlreadyTransmitedThisPeriod(true));
                    dispatch(setPayroll(response));
                    const responseConfirmBody: IConfirmResponse = {
                        idTrack: response.id_track,
                        successful: true,
                        descripcion: [],
                    };
                    if (response.status === REFUSED || response.status.toUpperCase() == PARTIALLY_TRANSMITED) {
                        const responseFormated: IElectronicPayrollResponse = JSON.parse(response?.response_service || '');
                        formatElectronicPayrollErrors(responseFormated, responseConfirmBody);
                        if (
                            responseConfirmBody.descripcion[0].includes(ELECTRONIC_PAYROLL_WITHOUT_DOCUMENTS) ||
                            responseConfirmBody.descripcion[0].includes(ELECTRONIC_PAYROLL_INSUFFICIENT_DOCUMENTS)
                        ) {
                            responseConfirmBody.descripcion[0] = ELECTRONIC_PAYROLL_BUY_NEW_PLANS;
                        }
                        dispatch(setResponseConfirm(responseConfirmBody));
                        dispatch(setErrorModal());
                        return;
                    }
                    dispatch(setResponseConfirm(responseConfirmBody));
                    return;
                }
            }
        } catch {
            dispatch(failedRequest('Error request data'));
        }
    };
};

/**
 * Function that allows to get the error description from electronic payroll response
 * @param electronicPayrollResponse electronic payroll response
 * @param responseConfirmBody structure to fullfill with information */
const formatElectronicPayrollErrors = (
    electronicPayrollResponse: IElectronicPayrollResponse,
    responseConfirmBody: IConfirmResponse
): void => {
    if (electronicPayrollResponse.trabajador.length === 0) {
        responseConfirmBody.descripcion.push(electronicPayrollResponse.descripcion);
        return;
    }
    const trabajadores = electronicPayrollResponse.trabajador;
    trabajadores.forEach(current => {
        const verifyDescriptionIsArray = current.mensajes[0].descripcion.includes('[');
        if (!verifyDescriptionIsArray) {
            responseConfirmBody.descripcion.push(
                `Empleado identificado con ${current.tipoDocumento} ${current.numeroDocumento}: ` +
                    current.mensajes[0].descripcion
            );
        } else {
            const messageInformation = JSON.parse(current.mensajes[0].descripcion);
            if (messageInformation.length > 0) {
                messageInformation.forEach((currentError: string) => {
                    responseConfirmBody.descripcion.push(
                        `Rechazado empleado identificado con ${current.tipoDocumento} ${current.numeroDocumento}: ` + currentError
                    );
                });
            }
        }
    });
};

let checkEmployerStatusPromise: Promise<void> | null = null;
/**
 * Action to check employer status
 */
export const getCheckEmployerStatus = (transmit?: boolean): AppThunk<Promise<void>> => {
    return async (dispatch, getState): Promise<void> => {
        if (checkEmployerStatusPromise) {
            return checkEmployerStatusPromise;
        }

        checkEmployerStatusPromise = (async (): Promise<void> => {
            const {
                utils: { document_type },
                plans: { activePlan },
                company: { information },
            } = getState();
            try {
                if (!information.document_type_id || !information?.document_number) {
                    dispatch(failedRequest('Error: Employer Information Missing'));
                    dispatch(setHasElectronicPayroll(false));
                    dispatch(setTransmitted(false));
                    return;
                }

                const isUsingFreePlan = activePlan?.free_Plan ?? false;
                const hasAlreadyElectronicPayroll = activePlan?.electronic_payroll ?? false;
                const getPayrollEmployerStatusResponse = await apiGetCheckEmployerStatus(
                    {
                        documentType: String(document_type?.find(type => type.id === information.document_type_id)?.code) || '',
                        documentNumber: information?.document_number || '',
                        isFreeUser: isUsingFreePlan,
                        electronicPayrollActive: hasAlreadyElectronicPayroll,
                    },
                    transmit
                );

                if (getPayrollEmployerStatusResponse.code !== PayrollResponseCodes.SUCCESS_OPERATION) {
                    if (
                        getPayrollEmployerStatusResponse.code ==
                        PayrollResponseCodes.EMPLOYER_HAS_NOT_ELECTRONIC_PAYRROLL_FOR_UPDATED
                    ) {
                        dispatch(failedRequest(''));
                        return;
                    }

                    dispatch(failedRequest('Error: Checking Employer Status'));
                    dispatch(setHasElectronicPayroll(false));
                    dispatch(setTransmitted(false));
                    return;
                }

                if (getPayrollEmployerStatusResponse.data.hasCurrentDocuments) {
                    const getPlansEmployerResponse = await apiGetPlansEmployee<IPlanApiResponse>();
                    if (
                        getPlansEmployerResponse.code !== PaymentResponseCodes.SUCCESS_OPERATION ||
                        !getPlansEmployerResponse.data
                    ) {
                        dispatch(failedRequest('Error: Payments not fetched'));
                        return;
                    }
                    const employerPlans = getPlansEmployerResponse.data;
                    const CompanyAprobedPlans = employerPlans.filter(plan => plan.payment_status === APPROVED);
                    dispatch(setPlansEmployee(CompanyAprobedPlans));
                }

                dispatch(failedRequest(''));
                dispatch(setHasElectronicPayroll(getPayrollEmployerStatusResponse.data.electronicPayrollEnable));
                dispatch(setTransmitted(checkEmployerCode(getPayrollEmployerStatusResponse.data)));
            } catch {
                dispatch(failedRequest('Error: Getting Employer Status'));
            } finally {
                checkEmployerStatusPromise = null;
            }
        })();

        return checkEmployerStatusPromise;
    };
};

/**
 * Action to get token electronic payroll
 */
export const getTokenElectronicPayroll = (): AppThunk => {
    return async (dispatch): Promise<void> => {
        try {
            const response = await apiGetElectronicPayrollToken();
            if (response) dispatch(setToken(response.data));
        } catch {
            dispatch(failedRequest('Error request data'));
        }
    };
};

/**
 * Action to get url enable electronic payroll
 */
export const getYearPeriodTransmit = (): AppThunk => {
    return async (dispatch): Promise<void> => {
        try {
            const response = await apiGetYearPeriodTransmit();
            if (response && (response as { id?: string })?.id) {
                dispatch(setYearPeriodId(response));
            }
        } catch {
            dispatch(failedRequest('Error request data'));
        }
    };
};

export const getPeriodsByYear = (): AppThunk => {
    return async (dispatch, getState): Promise<void> => {
        const {
            periods: { currentPeriod },
        } = getState();

        try {
            const { periods, period } = await getApiCarouselPeriods(PeriodicityTypes.MONTHLY, currentPeriod);
            if (periods?.length) {
                const electronicPayrollPeriods = [];
                const currentMonth = moment().format('MM');
                const currentYear = moment().year();
                const currentPeriodIndex = periods?.findIndex(period => period.code === `${currentYear}-${currentMonth}`);
                if (currentPeriodIndex != -1) {
                    if (currentPeriodIndex > 0) {
                        electronicPayrollPeriods.push(periods[currentPeriodIndex - 1]);
                    }
                    electronicPayrollPeriods.push(periods[currentPeriodIndex]);
                }
                dispatch(setPeriods(electronicPayrollPeriods));
            }
            dispatch(setCurrentIndex(period?.indexMonth));
        } catch {
            dispatch(failedRequest('Error request data'));
        }
    };
};

export const {
    setPayroll,
    setStartTransmit,
    setConfirmed,
    setHasElectronicPayroll,
    setCurrentTab,
    setErrorModal,
    setErrorGeneratePayrollModal,
    setResponseConfirm,
    setYearPeriodId,
    setToken,
    setTransmitted,
    setPeriods,
    setIsAlreadyTransmitedThisPeriod,
    failedRequest,
} = electronicPayrollSlice.actions;

export default electronicPayrollSlice.reducer;
